import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import logoStudent from '../assets/logo_student.png';
import fistImage from '../assets/fist.svg';
import InsightsReportModal from '../components/InsightsReportModal';
import { LineChart, Line, ResponsiveContainer, YAxis } from 'recharts';
import insightData from '../data/insight_json.json';
import addressRef from '../data/insight_address_ref.json';

function Insights() {
  const navigate = useNavigate();
  const [rankingData, setRankingData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTree, setSelectedTree] = useState('전체');
  const [treeNames, setTreeNames] = useState(['전체']);
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [selectedItemRatings, setSelectedItemRatings] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState([]);

  // 스크롤바를 숨기는 CSS 스타일 정의
  const hideScrollbarStyle = {
    scrollbarWidth: 'none', // Firefox
    msOverflowStyle: 'none', // IE and Edge
  };

  // Webkit 스크롤바 숨김 위한 스타일 요소 추가
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .hide-scrollbar::-webkit-scrollbar {
        display: none;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    // viewport 높이 설정
    const handleResize = () => {
      setViewportHeight(window.innerHeight);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const fetchRankingData = async () => {
      try {
        // 정적 JSON 데이터 처리
        const processedData = insightData
          .filter(item => item.report && Object.keys(item.report).length > 0) // report가 있는 데이터만
          .map((item, index) => {
            // 학년 변환 (숫자 → 문자열)
            let schoolGrade = '';
            if (item.school_grade <= 9) {
              schoolGrade = `${item.school_grade - 6}학년`;
            } else {
              schoolGrade = `${item.school_grade - 9}학년`;
            }

            // ratings 배열 생성 (exams 배열에서 추출)
            let ratings = [];
            let lastRating = 0;
            if (item.report && item.report.exams && Array.isArray(item.report.exams)) {
              // exams 배열에서 rating 값들을 추출 (exam_id 순으로 정렬)
              ratings = item.report.exams
                .sort((a, b) => a.exam_id - b.exam_id) // exam_id 오름차순 정렬 (낮은 id가 먼저)
                .map(exam => exam.rating || 0);
              // 가장 최신 rating (exam_id가 가장 높은 것)
              const highestExamIdItem = item.report.exams
                .reduce((max, exam) => exam.exam_id > max.exam_id ? exam : max, item.report.exams[0]);
              lastRating = highestExamIdItem?.rating || 0;
            }

            // exam_type 결정 (test 필드 사용)
            let examType = '기타';
            if (item.test && item.test.includes('중간')) {
              examType = '중간고사';
            } else if (item.test && item.test.includes('기말')) {
              examType = '기말고사';
            }

            return {
              report_id: item.exam_info && item.exam_info[0] ? item.exam_info[0].exam_id : `student_${item.student_id}`,
              student_name: item.student_name,
              school_name: item.school_name,
              school_grade: schoolGrade,
              school_grade_num: item.school_grade, // 원본 숫자 값 저장 (필터링용)
              last_rating: lastRating,
              ratings: ratings,
              rank: index + 1, // 순위는 임시로 인덱스 + 1
              exam_type: examType,
              test: item.test, // 원본 test 필드 보존
              score: parseInt(item.score) || 0, // 실제 시험 점수 추가
              report: item.report // 전체 report 데이터 보존
            };
          })
          .sort((a, b) => {
            // 1차: 실제 점수로 내림차순
            if (b.score !== a.score) {
              return b.score - a.score;
            }
            // 2차: 점수가 같으면 레이팅으로 내림차순
            return b.last_rating - a.last_rating;
          })
          .map((item, index) => ({ ...item, rank: index + 1 })); // 정렬 후 순위 재설정

        setRankingData(processedData);
        // 시험 유형 필터 옵션 설정
        setTreeNames(['전체', '중간고사', '기말고사', '중학교', '고등학교']);
        setIsLoading(false);
      } catch (error) {
        console.error('데이터를 불러오는데 실패했습니다:', error);
        setIsLoading(false);
      }
    };

    fetchRankingData();
  }, [navigate]);

  // 선택된 필터에 따라 데이터 필터링 및 독립적인 순위 부여
  const filteredRankingData = rankingData ? (() => {
    // 전체 선택이거나 필터가 없으면 전체 데이터 반환
    if (selectedFilters.length === 0) {
      return rankingData;
    }
    
    // 필터링 수행
    const filtered = rankingData.filter(item => {
      // 시험 유형 필터 확인
      const examFilters = selectedFilters.filter(f => f === '중간고사' || f === '기말고사');
      const schoolFilters = selectedFilters.filter(f => f === '중학교' || f === '고등학교');
      
      // 시험 유형 체크
      let examPass = examFilters.length === 0; // 시험 필터가 없으면 통과
      if (examFilters.length > 0) {
        examPass = examFilters.includes(item.exam_type);
      }
      
      // 학교 구분 체크
      let schoolPass = schoolFilters.length === 0; // 학교 필터가 없으면 통과
      if (schoolFilters.length > 0) {
        if (schoolFilters.includes('중학교') && item.school_grade_num <= 9) {
          schoolPass = true;
        }
        if (schoolFilters.includes('고등학교') && item.school_grade_num > 9) {
          schoolPass = true;
        }
      }
      
      // 두 조건 모두 만족해야 함
      return examPass && schoolPass;
    });
    
    // 필터링된 데이터에 대해 순위 재계산
    return filtered.map((item, index) => ({
      ...item,
      rank: index + 1
    }));
  })() : null;

  // 아이템 클릭 시 reportId, ratings, report 데이터 저장
  const handleItemClick = (reportId, ratings, reportData) => {
    setSelectedReportId(reportId);
    setSelectedItemRatings(ratings || []);
    // reportData를 sessionStorage에 저장 (ReportModal에서 사용)
    if (reportData) {
      sessionStorage.setItem('selectedReportData', JSON.stringify(reportData));
      sessionStorage.setItem('addressRef', JSON.stringify(addressRef));
    }
    setIsReportModalOpen(true);
  };

  const closeReportModal = () => {
    setIsReportModalOpen(false);
  };

  if (isLoading) return (
    <div className="relative flex flex-col min-h-screen">
      <div className="fixed inset-0 bg-[#fafafa] z-0" />
      <div className="relative z-20 flex flex-col min-h-screen">
        <main className="flex-grow flex items-center justify-center">
          <div className="flex flex-col items-center">
            <div className="w-12 h-12 border-4 border-gray-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="mt-4 text-gray-700 font-medium animate-pulse">로딩중...</p>
          </div>
        </main>
      </div>
    </div>
  );

  // 카드와 상단 콘텐츠의 예상 높이에 따른 계산
  const headerHeight = 280; // 상단 제목, 이미지, 필터 탭 영역 높이
  const paddingHeight = 60; // 상하 패딩 값
  const footerHeight = 50; // 저작권 문구 영역 높이
  const cardMaxHeight = viewportHeight - headerHeight - paddingHeight - footerHeight;

  return (
    <div className="fixed inset-0 bg-[#fafafa] flex flex-col" style={{ height: `${viewportHeight}px`}}>
      <div className="relative z-20 flex flex-col h-full overflow-hidden">
        <main className="flex flex-col items-center pt-[30px] pb-[10px] h-full">
          <div className="w-full max-w-[420px] mx-auto flex flex-col h-full" style={{ paddingLeft: '14.4px', paddingRight: '14.4px' }}>
            <div className="mb-4 text-center">
              <h1 className="text-[32px] font-extrabold text-[#231F20] mb-2"><span className="relative"><span className="relative text-[#FF6B6B]">실<span className="absolute -top-4 left-1/2 transform -translate-x-1/2 text-[#FF6B6B] text-[16px]">•</span></span><span className="relative text-[#FF6B6B]">제<span className="absolute -top-4 left-1/2 transform -translate-x-1/2 text-[#FF6B6B] text-[16px]">•</span></span></span> 성적 vs <span className="relative"><span className="relative text-[#4ABC85]">평<span className="absolute -top-4 left-1/2 transform -translate-x-1/2 text-[#4ABC85] text-[16px]">•</span></span><span className="relative text-[#4ABC85]">가<span className="absolute -top-4 left-1/2 transform -translate-x-1/2 text-[#4ABC85] text-[16px]">•</span></span></span> 성적</h1>
              <p className="text-[14px] font-normal text-[#7B7979]">중간고사, 기말고사와 리포트 결과를 비교해보세요</p>
            </div>
            
            <div className="flex justify-center mb-3">
              <img src={fistImage} alt="주먹 이미지" width="169" height="165" />
            </div>

            {/* 시험 유형 필터 탭 */}
            <div className="mb-4">
              <div className="flex flex-wrap gap-2 justify-center">
                {treeNames.map((examType) => (
                  <button
                    key={examType}
                    onClick={() => {
                      if (examType === '전체') {
                        // 전체 클릭 시 모든 필터 초기화
                        setSelectedFilters([]);
                      } else {
                        // 다른 탭 클릭 시 토글
                        setSelectedFilters(prev => {
                          if (prev.includes(examType)) {
                            // 이미 선택된 경우 제거
                            return prev.filter(f => f !== examType);
                          } else {
                            // 선택되지 않은 경우 추가
                            return [...prev, examType];
                          }
                        });
                      }
                    }}
                    className={`px-3 py-1.5 rounded-full text-[12px] font-medium transition-colors ${
                      examType === '전체' && selectedFilters.length === 0
                        ? 'bg-[#4ABC85] text-white'
                        : selectedFilters.includes(examType)
                        ? 'bg-[#4ABC85] text-white'
                        : 'bg-[#F5F5F5] text-[#7B7979] hover:bg-[#EEEEEE]'
                    }`}
                  >
                    {examType}
                  </button>
                ))}
              </div>
            </div>

            <div
              className="w-full max-w-[391.2px] mx-auto rounded-3xl p-4 shadow-[0_0_6px_rgba(0,0,0,0.1)] mb-4 flex flex-col overflow-hidden" 
              style={{ maxHeight: `${cardMaxHeight}px`, height: 'auto' }}
            >
              <div className="space-y-4 overflow-y-auto hide-scrollbar" style={hideScrollbarStyle}>
                {filteredRankingData && filteredRankingData.map((item, index) => (
                  <div 
                    key={item.report_id}
                    className={`flex items-center p-[7.2px] rounded-3xl bg-[#fafafa] hover:bg-[#4ABC85]/10 cursor-pointer transition-colors`}
                    onClick={() => handleItemClick(item.report_id, item.ratings, item.report)}
                  >
                      <div className="flex items-center flex-1">
                        <span className={`w-5 text-center text-[14px] font-bold mr-3 ${
                          index < 3 
                            ? 'text-green-600' 
                            : 'text-[#A3A1A2] font-normal'
                        }`}>{item.rank}</span>
                        {/* <img 
                          src={logoStudent} 
                          alt="학생 로고" 
                          className="w-[30px] h-[30px] mr-3"
                        /> */}
                        <div>
                          <p className="text-[12px] font-semibold text-[#231F20] select-none pointer-events-none">{item.student_name}</p>
                          <p className="text-[8px] text-[#7B7979]">
                            <span className="font-bold">{item.school_name}</span>
                            <br />
                            <span className="font-bold">{item.school_grade}</span>
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex flex-col items-end gap-1 ml-6">
                        {/* 실제 성적 vs 평가 성적 - 칼럼 정렬 */}
                        <div className="flex items-start gap-4">
                          {/* 실제 칼럼 */}
                          <div className="text-center flex flex-col justify-center">
                            <p className="text-[8px] text-[#7B7979]">실제</p>
                            <p className="text-[18px] text-[#FF6B6B]">
                              <span className="font-bold">{item.score}</span>
                              <span className="text-[10px] text-[#A3A1A2]">점</span>
                            </p>
                            <p className="text-[7px] text-[#A3A1A2]">
                              {item.test ? `(${item.test})` : ''}
                            </p>
                          </div>
                          
                          <div className="text-[10px] text-[#A3A1A2] mt-3">vs</div>
                          
                          {/* 평가 칼럼 */}
                          <div className="text-center flex flex-col justify-center">
                                                          <p className="text-[8px] text-[#7B7979]">평가</p>
                            <p className={`text-[18px] ${
                              index < 3 
                                ? 'text-green-600' 
                                : 'text-[#231F20]'
                            }`}>
                              <span className="font-bold">{String(item.last_rating).slice(0, 2)}</span>
                              <span className="text-[10px] text-[#A3A1A2]">{String(item.last_rating).slice(2)}</span>
                            </p>
                            {/* 평가 칼럼 하단에 그래프 */}
                            <div className="w-[35px] h-[15px] mt-1">
                              {item.ratings.length > 1 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                  <LineChart 
                                    data={[...item.ratings].map(rating => ({ value: rating }))} 
                                    margin={{ top: 2, right: 2, left: 2, bottom: 2 }}
                                  >
                                    <Line 
                                      type="monotone" 
                                      dataKey="value" 
                                      stroke={index < 3 ? "#4ABC85" : "#A3A1A2"} 
                                      strokeWidth={1} 
                                      dot={false} 
                                      isAnimationActive={false}
                                    />
                                    <YAxis 
                                      domain={[
                                        Math.min(...item.ratings) - Math.max(200, Math.min(...item.ratings) * 0.1), 
                                        Math.max(...item.ratings) + Math.max(200, Math.max(...item.ratings) * 0.1)
                                      ]} 
                                      hide={true}
                                    />
                                  </LineChart>
                                </ResponsiveContainer>
                              ) : (
                                <div className="w-full h-full"></div>
                              )}
                            </div>
                          </div>
                          
                          {/* 일치율 칼럼 */}
                          <div className="text-center ml-4 bg-[#4ABC85]/10 rounded-3xl px-3 py-2 flex flex-col justify-center self-center w-[70px]">
                                                          <p className="text-[8px] text-[#7B7979]">실제/평가</p>
                            <p className="text-[14px] font-bold text-[#4ABC85]">
                              {Math.round((item.score / Math.floor(item.last_rating / 100)) * 100)}%
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            
            <div className="mt-auto pt-2 text-center">
              <p className="text-[8px] text-[#A3A1A2]">© Copyright 2025 SKY미래연수학학원. All Rights Reserved</p>
            </div>
          </div>
        </main>
      </div>

      {/* 리포트 모달 */}
      <InsightsReportModal 
        isOpen={isReportModalOpen} 
        onClose={closeReportModal}
        reportId={selectedReportId}
        ratings={selectedItemRatings}
      />
    </div>
  );
}

export default Insights; 
